<template>
  <div />
</template>

<script>

/**
 * Vue: Nosto api call (nosto-api-call)
 * -----------------------------------------------------------------------------
 * Component to make Nosto API calls.
 * https://docs.nosto.com/techdocs/implementing-nosto/implement-search/implement-search-using-api/implementing-search-page
 *
 * @param {String} choices - Formatted filter choices to use in API call.
 * @param {Number} collectionId - Collection id to use for 'filter' endpoint.
 * @param {Number} limit - Number of products to retrieve per page.
 * @param {Number} page - Current page number.
 * @param {String} query - Search query.
 * @param {String} storageKey - Storage key used for temp collection storage.
 *
 */
import productQuery from '~/graphql/queries/nosto-data.gql'
import { formatLocalisedPriceData } from '~/helpers/format-nosto-api'
import { waitFor } from '~/helpers/general'

export default {
  name: 'NostoApiCall',

  props: {
    choices: {
      type: String,
      default: '',
    },
    collectionId: {
      type: Number,
      default: null,
    },
    isLoadMoreClicked: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: Number,
      default: 16,
    },
    query: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    storageKey: {
      type: String,
      default: 'collection-temp',
    },
  },

  emits: {
    'nosto-updated': (payload) => typeof payload === 'object',
    'nosto-prices-updated': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      global: true,
      sessionCookie: false,
    }
  },

  watch: {

    /**
     * Watch choices update to get product results.
     * - Ignore update if products and choices are being set from storage.
     */
    choices(choices) {
      if (sessionStorage.getItem(this.storageKey)) {
        sessionStorage.removeItem(this.storageKey)
        return
      }

      this.getProductResults(choices)
    },

    /**
     * Watch query update to get product results.
     */
    query() {
      if (this.type !== 'autocomplete') {
        return
      }

      this.getProductResults(this.choices)
    },
  },

  beforeMount() {
    this.setSessionCookieData('nostoSearchSessionParams')

    this.$nextTick().then(() => {
      if (!this.sessionCookie) {
        this.setNostoSession()
      }
    })
  },

  methods: {

    /**
     * Handle API call to Nosto.
     * @param {String} choices - Search parameters to add to request url.
     */
    getProductResults(choices) {
      if (!this.sessionCookie) {
        return
      }

      try {

        /**
         * Calculate new page to load to get action.
         */
        const params = new URLSearchParams(choices)
        let searchType = 'serp'
        let newPage = 1

        if (params.get('page')) {
          newPage = Number(params.get('page'))
        }

        const filters = []

        const filterParams = choices
          .split('&')
          .filter(param => !param.startsWith('page=') && param.includes('='))

        /**
         * Convert raw parameters into Nosto-compatible filters.
         */
        for (const param of filterParams) {
          const [rawKey, rawValue] = param.split('=')

          const field = rawKey
            .replace('pf_', '')
            .replace('%5B%5D', '')
            .replace('%5b%5d', '')
            .replace('[]', '')
            .replaceAll('__', '.')

          const value = decodeURIComponent(rawValue || '')

          if (!field || field === 'sort') {
            continue
          }

          if (field === 'listPrice') {
            const splitCharacter = value.includes('%3a')
              ? '%3a'
              : '%3A'

            const [gt, lt] = value.split(splitCharacter)

            filters.push({
              field,
              range: { gt, lt },
            })

            continue
          }

          const existingFilter = filters.find((filter) => filter.field === field)

          if (existingFilter) {
            existingFilter.value.push(value)

          } else {
            filters.push({
              field,
              value: [value],
            })
          }
        }

        /**
         * Get sort by option.
         */
        let sortBy = false
        let sortOrder = 'asc'
        const sortingParam = params.get('sort')

        if (sortingParam) {
          if (sortingParam.includes('descending')) {
            sortOrder = 'desc'
            sortBy = sortingParam.replace('-descending', '')
          } else {
            sortOrder = 'asc'
            sortBy = sortingParam.replace('-ascending', '')
          }
        }

        /**
         * Construct product data.
         */
        const productData = {
          size: this.limit,
          from: newPage === 1 ? 0 : this.limit * (newPage - 1),
          facets: ['*'],
          fields: [
            'available',
            'priceCurrencyCode',
            'listPrice',
            'price',
            'productId',
            'url',
            'name',
            'tags1',
            'tags2',
            'tags3',
            'alternateImageUrls',
            'description',
            'imageUrl',
            'brand',
            'datePublished',
            'customFields.key',
            'customFields.value',
            'skus.id',
            'skus.name',
            'skus.listPrice',
            'skus.price',
            'skus.availability',
            'skus.customFields.key',
            'skus.customFields.value',
          ],
        }

        if (this.type === 'collection') {
          productData.categoryId = this.collectionId?.toString() || ''
          searchType = 'category'
        } else if (this.type === 'autocomplete') {
          searchType = 'autocomplete'
        }

        if (filters.length > 0) {
          productData.filter = filters
        }

        if (sortBy) {
          productData.sort = [
            {
              field: sortBy.replace('price', 'customFields.custom-min_price'),
              order: sortOrder,
            },
          ]
        }

        const searchParams = {
          products: productData,
          keywords: {
            fields: ['keyword'],
            size: 5,
          },
          sessionParams: JSON.parse(decodeURIComponent(this.sessionCookie)),
        }

        if (this.query) {
          searchParams.query = decodeURIComponent(this.query)
        }

        /**
         * Query data.
         */
        const callNosto = () => {
          nostojs(api => {
            api.search(searchParams, {
              redirect: searchType !== 'autocomplete',
              track: searchType,
            }).then(async(response) => {

              /**
               * Emit event.
               * - Do not render prices.
               */
              this.$emit('nosto-updated', {
                ...response.products,
                suggestions: response?.keywords?.hits,
                action: this.isLoadMoreClicked ? 'append' : 'replace',
                page: newPage,
              })

              /**
               * Emit separate event with update prices.
               */
              if (response.products.hits) {
                const prices = await this.getConvertedProductPrices(response.products.hits)
                this.$emit('nosto-prices-updated', prices)
              }
            })
          })
        }

        if (window.nostojs) {
          callNosto()
        } else {
          waitFor('nostojs', callNosto)
        }

      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     * Convert product price for Shopify markets.
     * - Nosto does not handle markets fully.
     * - An extra network call was required to enable market pricing.
     * @param {object} data - Product data.
     * @returns {Promise}
     */
    getConvertedProductPrices(data) {
      return new Promise(async(resolve, reject) => {
        if (!data.length) {
          resolve([])
        }

        const queryString = data.map((product) => {
          return `id:${product.productId}`
        }).join(' OR ')

        try {
          const response = await cnvs.Query({
            query: productQuery,
            variables: {
              country: cnvs.store.country,
              language: cnvs.store.language,
              query: queryString,
            },
          })

          if (!response.products?.edges?.length) {
            resolve([])
            return
          }

          const prices = response.products.edges.map((product) => {
            return formatLocalisedPriceData(product)
          })

          resolve(prices)

        } catch (error) {
          reject(error)
        }
      })
    },

    setNostoSession() {
      const callNosto = () => {
        nostojs(api => {
          api.getSearchSessionParams().then(response => {
            this.sessionCookie = encodeURIComponent(JSON.stringify(response))
            document.cookie = `nostoSearchSessionParams=${this.sessionCookie}`

            this.$nextTick().then(() => {
              this.getProductResults(this.choices)
            })
          })
        })
      }

      if (window.nostojs) {
        callNosto()
      } else {
        waitFor('nostojs', callNosto)
      }
    },

    setSessionCookieData(name) {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)

      if (parts.length === 2) {
        this.sessionCookie = parts.pop().split(';').shift()

        return
      }

      this.sessionCookie = false
    },
  },
}
</script>
